import React from 'react'
import { useOutletContext } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Star,
  MessageSquare,
  Calendar,
  BarChart3,
  CheckCircle,
  ChevronRight,
  Phone,
  PlusCircle,
  Globe,
  ExternalLink,
  Search,
  Filter,
  FileText,
  HelpCircle
} from 'lucide-react'
import { SyncStatusWidget } from '@/features/gmb/components/shared/SyncStatusWidget'
import { useGMBStats, useGMBRecentActivity } from '@/hooks/useGMBStats'
import { formatDistanceToNow } from 'date-fns'

interface OutletContext {
  locations: any[]
  selectedLocation: string | null
  setSelectedLocation: (id: string) => void
  selectedLocationData: any
}

export default function GMBOverview() {
  const { locations, selectedLocation, setSelectedLocation, selectedLocationData } = useOutletContext<OutletContext>()

  // Fetch real statistics and activity
  const { data: gmbStats } = useGMBStats()
  const { data: recentActivity, isLoading: activityLoading } = useGMBRecentActivity(5)

  return (
    <div className="space-y-4">
      {/* Sync Status Widget */}
      <SyncStatusWidget 
        detailed={true}
        enableQuickSync={false}
      />

      {/* Location Selector */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Locations</CardTitle>
              <CardDescription>Select a location to manage</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Search className="mr-2 h-4 w-4" />
                Search
              </Button>
              <Button variant="outline" size="sm">
                <Filter className="mr-2 h-4 w-4" />
                Filter
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {locations?.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {locations.map((location) => (
              <div
                key={location.id}
                className={`relative rounded-lg border p-4 cursor-pointer transition-colors hover:bg-accent ${
                  selectedLocation === location.id ? 'border-primary bg-accent' : ''
                }`}
                onClick={() => setSelectedLocation(location.id)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-sm">
                      {location.name || location.account_name || 'Unnamed Location'}
                    </h4>
                    <p className="text-xs text-muted-foreground">{location.address}</p>
                  </div>
                  {location.place_id && (
                    <Badge variant="secondary" className="text-xs">Verified</Badge>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  {location.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span>Phone</span>
                    </div>
                  )}
                  {location.website && (
                    <div className="flex items-center gap-1">
                      <Globe className="h-3 w-3" />
                      <span>Website</span>
                    </div>
                  )}
                </div>
                {selectedLocation === location.id && (
                  <div className="absolute top-2 right-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                  </div>
                )}
              </div>
            ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No locations found</p>
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/integrations/gmb/sync'}
              >
                Sync Locations
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedLocationData && (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Location Details */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {selectedLocationData.name || selectedLocationData.account_name || 'Unnamed Location'}
                </CardTitle>
                <CardDescription>Location Information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm font-medium mb-1">Address</div>
                  <p className="text-sm text-muted-foreground">{selectedLocationData.address}</p>
                </div>
                {selectedLocationData.phone && (
                  <div>
                    <div className="text-sm font-medium mb-1">Phone</div>
                    <p className="text-sm text-muted-foreground">{selectedLocationData.phone}</p>
                  </div>
                )}
                {selectedLocationData.website && (
                  <div>
                    <div className="text-sm font-medium mb-1">Website</div>
                    <a 
                      href={selectedLocationData.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      Visit Website
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                )}
                <div className="pt-4">
                  <Button className="w-full">
                    Edit Location Info
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common tasks for this location</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="hidden sm:block">
                  <div className="space-y-2">
                    <Button variant="outline" className="w-full justify-start">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Respond to Reviews
                      {(gmbStats?.pendingResponses ?? 0) > 0 && (
                        <Badge variant="destructive" className="ml-auto">
                          {gmbStats?.pendingResponses} new
                        </Badge>
                      )}
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <PlusCircle className="mr-2 h-4 w-4" />
                      Create New Post
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Calendar className="mr-2 h-4 w-4" />
                      Update Business Hours
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <BarChart3 className="mr-2 h-4 w-4" />
                      View Performance Report
                    </Button>
                  </div>
                </div>
                <div className="sm:hidden">
                  <ScrollArea className="w-full">
                    <div className="flex gap-2 pb-2">
                      <Button variant="outline" className="min-w-[220px] justify-start h-auto p-3">
                        <MessageSquare className="mr-2 h-4 w-4" />
                        <div className="text-left">
                          <div className="text-sm font-medium leading-tight">Respond to Reviews</div>
                          <div className="text-xs text-muted-foreground">Handle new feedback</div>
                        </div>
                        {(gmbStats?.pendingResponses ?? 0) > 0 && (
                          <Badge variant="destructive" className="ml-2">
                            {gmbStats?.pendingResponses}
                          </Badge>
                        )}
                      </Button>
                      <Button variant="outline" className="min-w-[220px] justify-start h-auto p-3">
                        <PlusCircle className="mr-2 h-4 w-4" />
                        <div className="text-left">
                          <div className="text-sm font-medium leading-tight">Create New Post</div>
                          <div className="text-xs text-muted-foreground">Share updates</div>
                        </div>
                      </Button>
                      <Button variant="outline" className="min-w-[220px] justify-start h-auto p-3">
                        <Calendar className="mr-2 h-4 w-4" />
                        <div className="text-left">
                          <div className="text-sm font-medium leading-tight">Update Hours</div>
                          <div className="text-xs text-muted-foreground">Business availability</div>
                        </div>
                      </Button>
                      <Button variant="outline" className="min-w-[220px] justify-start h-auto p-3">
                        <BarChart3 className="mr-2 h-4 w-4" />
                        <div className="text-left">
                          <div className="text-sm font-medium leading-tight">Performance Report</div>
                          <div className="text-xs text-muted-foreground">Insights & trends</div>
                        </div>
                      </Button>
                    </div>
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest updates across all locations</CardDescription>
            </CardHeader>
            <CardContent>
              {activityLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-start gap-4">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-48" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentActivity && recentActivity.length > 0 ? (
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div key={`${activity.type}-${activity.id}`} className="flex items-start gap-4">
                      <div className={`rounded-full p-2 ${
                        activity.type === 'review'
                          ? 'bg-yellow-500/10'
                          : activity.type === 'post'
                          ? 'bg-green-500/10'
                          : 'bg-blue-500/10'
                      }`}>
                        {activity.type === 'review' ? (
                          <Star className={`h-4 w-4 ${
                            (activity.metadata?.rating ?? 0) >= 4 ? 'text-yellow-500' : 'text-orange-500'
                          }`} />
                        ) : activity.type === 'post' ? (
                          <FileText className="h-4 w-4 text-green-500" />
                        ) : (
                          <HelpCircle className="h-4 w-4 text-blue-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{activity.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {activity.description}
                          {activity.timestamp && ` • ${formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}`}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm">
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No recent activity found</p>
                  <p className="text-xs mt-1">Sync your locations to see activity</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
